import { Alert, Button, Form, Input } from "antd";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { AuthLayout } from "@/components/AuthLayout";
import { useI18n } from "@/i18n";

export function ForgotPassword() {
  const { t } = useI18n();
  const errorText = useErrorText();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async ({ email }: { email: string }) => {
    setSubmitting(true);
    setError(null);
    try {
      await unwrap(api.POST("/v1/auth/forgot-password", { body: { email } }));
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title={t("auth.forgot.title")} subtitle={sent ? undefined : t("auth.forgot.body")}>
      {sent ? (
        <Alert type="success" showIcon title={t("auth.forgot.sent")} />
      ) : (
        <>
          {error !== null && <Alert type="error" showIcon title={errorText(error)} style={{ marginBottom: 16 }} />}
          <Form layout="vertical" onFinish={submit} requiredMark={false} disabled={submitting}>
            <Form.Item name="email" label={t("common.email")} rules={[{ required: true, type: "email", message: t("field.invalid_email") }]}>
              <Input autoComplete="email" autoFocus inputMode="email" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={submitting}>
              {t("auth.forgot.submit")}
            </Button>
          </Form>
        </>
      )}
      <div style={{ marginTop: 16, textAlign: "center" }}>
        <Link to="/login">{t("common.back")}</Link>
      </div>
    </AuthLayout>
  );
}

type ResetValues = { password: string; confirm: string };

export function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<ResetValues>();
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const token = params.get("token") ?? "";

  const submit = async ({ password }: ResetValues) => {
    setSubmitting(true);
    setError(null);
    try {
      await unwrap(api.POST("/v1/auth/reset-password", { body: { token, password } }));
      setDone(true);
    } catch (err) {
      if (!applyFieldErrors(form, err)) setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title={t("auth.reset.title")}>
      {done ? (
        <>
          <Alert type="success" showIcon title={t("auth.reset.done")} style={{ marginBottom: 16 }} />
          <Link to="/login">
            <Button type="primary" block>
              {t("auth.login.submit")}
            </Button>
          </Link>
        </>
      ) : (
        <>
          {error !== null && <Alert type="error" showIcon title={errorText(error)} style={{ marginBottom: 16 }} />}
          <Form form={form} layout="vertical" onFinish={submit} requiredMark={false} disabled={submitting || !token}>
            <Form.Item
              name="password"
              label={t("auth.reset.newPassword")}
              extra={t("field.password_hint")}
              rules={[{ required: true, min: 10, message: t("field.password_hint") }, { max: 128, message: t("field.too_long") }]}
            >
              <Input.Password autoComplete="new-password" autoFocus />
            </Form.Item>
            <Form.Item
              name="confirm"
              label={t("auth.reset.confirm")}
              dependencies={["password"]}
              rules={[
                { required: true, message: t("field.required") },
                ({ getFieldValue }) => ({
                  validator: (_, v) =>
                    !v || v === getFieldValue("password") ? Promise.resolve() : Promise.reject(new Error(t("field.password_mismatch"))),
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={submitting}>
              {t("auth.reset.submit")}
            </Button>
          </Form>
        </>
      )}
    </AuthLayout>
  );
}
